
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  formatDateNormal,
  formatPriceRP,
  INTERNET_ERROR,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { POSLeaderboardController } from "@/app/ui/controllers/POSLeaderboardController";
import { POSLeaderboardListRequest } from "@/data/payload/api/POSLeaderboardApiRequest";
import {
  Cursor,
  Leaderboard,
  PosLeaderboardCofig
} from "@/domain/entities/PosLeaderboard";
import { Options, prop, Vue } from "vue-class-component";

class Props {
  period = prop<string>({
    default: formatDateNormal(new Date(), "YYYY-MM"),
    type: String
  });
  scope = prop<string>({
    default: "NATIONAL"
  });
  config = prop<PosLeaderboardCofig[]>({
    default: () => [],
    type: Array
  });
  error = prop<string>({
    default: "",
    type: String
  });
  areaCode = prop<string>({
    default: "",
    type: String
  });
}

@Options({
  emits: ["retry"],
  watch: {
    payload: {
      async handler() {
        await this.disconnectObservers();
        this.isTopRankIntersecting = false;
        this.isNearestTopRewardIntersecting = false;
        this.leaderboards = [];
        this.getList(null, false);
      },
      deep: true
    },
    config: {
      handler() {
        if (!this.leaderboards.length) return;
        const nearestRankEl = document.getElementById("next-tier");

        this.nearestTopRankObserver = new IntersectionObserver(
          this.observeNearestTopRank,
          {
            root: document.getElementById("pos-leaderboard")
          }
        );
        if (nearestRankEl) this.nearestTopRankObserver.observe(nearestRankEl);
      },
      deep: true
    }
  }
})
class PosLeaderboardTable extends Vue.with(Props) {
  formatPriceRP = formatPriceRP;
  leaderboards: Leaderboard[] = [];
  cursor = new Cursor();
  isCurrentRankInTop = false;
  isTopRankIntersecting = false;
  isNearestTopRewardIntersecting = false;
  bottomObserver: IntersectionObserver | null = null;
  currentRankObserver: IntersectionObserver | null = null;
  topRankObserver: IntersectionObserver | null = null;
  nearestTopRankObserver: IntersectionObserver | null = null;
  isCurrentRankInTopIntersecting = true;
  isLoading = false;
  previousScrollHeightMinusScrollTop = 0;
  isInRewardMode = true;

  unmounted() {
    if (this.topRankObserver) this.topRankObserver.disconnect();
    if (this.currentRankObserver) this.currentRankObserver.disconnect();
    if (this.bottomObserver) this.bottomObserver.disconnect();
    if (this.nearestTopRankObserver) this.nearestTopRankObserver.disconnect();
  }

  get payload() {
    return {
      scope: this.scope,
      period: this.period
    };
  }

  get tierRankEnds() {
    const scope = this.config.find((c) => c.scope === this.scope);
    const areaCode = scope?.tier[0].areaCode;
    if (areaCode && this.areaCode) {
      return this.config
        .find((config) => config.scope === this.scope)
        ?.tier.filter((tier) => tier.areaCode === this.areaCode)
        .map((tier) => {
          return tier.rankEnd;
        });
    }
    return this.config
      .find((config) => config.scope === this.scope)
      ?.tier.map((tier) => {
        return tier.rankEnd;
      });
  }

  get nextTier() {
    const currentRank =
      this.leaderboards.find((l) => l.isCurrentPos)?.rank || 0;
    const scope = this.config.find((c) => c.scope === this.scope);
    const tier =
      scope?.tier.findIndex(
        (t) => t.rankStart <= currentRank && t.rankEnd >= currentRank
      ) || 0;
    let areaCode = scope?.tier[0].areaCode;

    if (areaCode) {
      areaCode =
        scope?.tier.find((t) => t.areaCode === this.areaCode)?.areaCode || "";
    }

    if (areaCode && areaCode === this.areaCode) {
      return (
        scope?.tier.filter((t) => t.areaCode === areaCode)[
          Math.max(0, tier - 1)
        ]?.rankEnd || 0
      );
    }
    return scope?.tier[Math.max(0, tier - 1)]?.rankEnd || 0;
  }

  get lastRankEnd() {
    const scope = this.config.find((c) => c.scope === this.scope);
    const tier = scope?.tier || [];
    const areaCode = tier[0]?.areaCode;
    if (areaCode && this.areaCode) {
      return scope?.tier.filter((tier) => tier.areaCode === this.areaCode)[
        scope?.tier.filter((tier) => tier.areaCode === this.areaCode).length - 1
      ]?.rankEnd;
    }
    return scope?.tier[scope?.tier.length - 1]?.rankEnd;
  }

  get columns() {
    return [
      {
        key: "rank",
        style: {
          width: "102px",
          textAlign: "left",
          paddingLeft: "2rem"
        }
      },
      {
        key: "posName",
        style: {
          width: "200px",
          textAlign: "left"
        }
      },
      {
        key: "achievement",
        style: {
          width: "176px",
          textAlign: "left"
        }
      },
      {
        key: "cityCode",
        style: {
          width: "78px",
          textAlign: "left"
        }
      },
      {
        key: "totalRevenue",
        style: {
          width: "120px",
          textAlign: "left"
        }
      },
      {
        key: "estimatedReward",
        style: {
          width: "120px",
          textAlign: "left"
        }
      }
    ];
  }

  get tableData() {
    return this.leaderboards;
  }

  isRankDeltaNegative(rank = 0) {
    return rank < 0;
  }

  toAbs(number = 0) {
    return Math.abs(number);
  }

  errorList = "";
  async getList(cursor: null | number = null, top = true, limit = 20) {
    this.errorList = "";

    if (!this.period) return;

    try {
      if (this.isLoading) return;
      MainAppController.showLoading();
      this.isLoading = true;

      await this.$nextTick();
      const container = document.getElementById("pos-leaderboard");
      if (container) {
        this.previousScrollHeightMinusScrollTop =
          container?.scrollHeight - container?.scrollTop;
      }
      const resp = await POSLeaderboardController.getLeaderboardList(
        new POSLeaderboardListRequest({
          cursor: cursor,
          scope: this.scope,
          period: this.period,
          limit: limit
        })
      );

      this.isInRewardMode = resp.meta.mode === "IN_REWARD";

      if (top) {
        this.leaderboards = [...resp.leaderboard, ...this.leaderboards];
        if (container) {
          await this.$nextTick();
          container.scrollTop =
            container.scrollHeight - this.previousScrollHeightMinusScrollTop;
        }
      } else {
        this.leaderboards = [...this.leaderboards, ...resp.leaderboard];
      }

      if (!this.isInRewardMode) {
        this.isTopRankIntersecting = true;
        this.isNearestTopRewardIntersecting = true;
        this.isCurrentRankInTopIntersecting = true;

        if (resp.leaderboard.length) {
          this.leaderboards.splice(
            this.leaderboards.length - 1,
            0,
            new Leaderboard({
              name: "-",
              rank: 1
            })
          );
          this.leaderboards.splice(
            this.leaderboards.length - 3,
            0,
            new Leaderboard({
              name: "-",
              rank: 1
            })
          );
        }
      }

      this.cursor.next = resp.meta.cursor.next;
      this.cursor.previous = resp.meta.cursor.previous;
    } catch (error) {
      this.errorList = parsingErrorResponse(error).type;
    } finally {
      this.isLoading = false;
      await this.$nextTick();

      const bottomObserverEl = document.getElementById(
        "infinite-scroll-bottom"
      );
      const currentRankObserverEl = document.getElementById("current-rank-row");
      const topRankObserverEl = document.getElementById("top-rank-row");
      const nearestRankEl = document.getElementById("next-tier");
      const currentRankData = this.leaderboards.find((l) => l.isCurrentPos);

      if (currentRankData?.rank === 1) this.isTopRankIntersecting = true;
      if (currentRankData?.tier === 1)
        this.isNearestTopRewardIntersecting = true;

      await this.$nextTick();

      if (bottomObserverEl) {
        this.bottomObserver = new IntersectionObserver(this.observeBottom, {
          root: document.getElementById("pos-leaderboard")
        });
        this.bottomObserver.observe(bottomObserverEl);
      }

      if (currentRankObserverEl) {
        this.currentRankObserver = new IntersectionObserver(
          this.observeCurrentRank,
          {
            root: document.getElementById("pos-leaderboard")
          }
        );
        this.currentRankObserver.observe(currentRankObserverEl);
      }

      if (topRankObserverEl) {
        this.topRankObserver = new IntersectionObserver(this.observeTopRank, {
          root: document.getElementById("pos-leaderboard")
        });
        this.topRankObserver.observe(topRankObserverEl);
      }

      if (nearestRankEl) {
        this.nearestTopRankObserver = new IntersectionObserver(
          this.observeNearestTopRank,
          {
            root: document.getElementById("pos-leaderboard")
          }
        );
        this.nearestTopRankObserver.observe(nearestRankEl);
      }
      MainAppController.closeLoading();
    }
  }

  async scrollToCurrentRank() {
    await this.$nextTick();
    const row = document.querySelector(".highlight");
    if (row) {
      row.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }
  }

  observeTop(
    entries: IntersectionObserverEntry[],
    _observer: IntersectionObserver
  ) {
    entries.forEach((entry) => {
      if (!this.cursor.previous) return;
      if (entry.isIntersecting) {
        this.getList(this.cursor.previous);
      }
    });
  }

  observeNearestTopRank(
    entries: IntersectionObserverEntry[],
    _observer: IntersectionObserver
  ) {
    entries.forEach((entry) => {
      const rect = entry.boundingClientRect;
      const root = entry.rootBounds;
      this.isNearestTopRewardIntersecting = entry.isIntersecting;

      if (!root) return;

      if (rect.top <= root.top) {
        this.isNearestTopRewardIntersecting = entry.isIntersecting;
      } else if (rect.bottom >= root.bottom) {
        this.isNearestTopRewardIntersecting = true;
      }
    });
  }

  observeBottom(
    entries: IntersectionObserverEntry[],
    _observer: IntersectionObserver
  ) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.isLoading) {
        if (this.lastRankEnd && this.cursor.next)
          this.getList(
            this.leaderboards[this.leaderboards.length - 1].rank + 1,
            false
          );
      }
    });
  }

  observeCurrentRank(
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver
  ) {
    this.isCurrentRankInTopIntersecting = true;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        this.isCurrentRankInTopIntersecting = false;
      } else {
        this.isCurrentRankInTopIntersecting = true;
      }
      const rect = entry.boundingClientRect;
      const root = entry.rootBounds;

      if (!root) return;

      if (rect.top <= root.top) {
        this.isCurrentRankInTop = true;
      } else if (rect.bottom >= root.bottom) {
        this.isCurrentRankInTop = false;
      }
    });
  }

  observeTopRank(
    entries: IntersectionObserverEntry[],
    _observer: IntersectionObserver
  ) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.isTopRankIntersecting = true;
      } else {
        this.isTopRankIntersecting = false;
      }
    });
  }

  async scrollToTopRank() {
    const topRankObserverEl = this.$refs["infinite-scroll-top-rank"] as Element;
    if (topRankObserverEl) {
      topRankObserverEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    } else {
      await this.getList(1, true, this.leaderboards[0].rank - 1);
      await this.$nextTick();
      this.scrollToTopRank();
    }
  }

  async scrollToNearestTopRank() {
    const el = document.getElementById("next-tier");
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    } else {
      if (this.nextTier) {
        await this.getList(
          this.nextTier,
          true,
          this.leaderboards[0].rank - this.nextTier
        );
        await this.$nextTick();
        this.scrollToNearestTopRank();
      }
    }
  }

  get noConnection() {
    const errorInternet = this.error || this.errorList;
    return (
      errorInternet === INTERNET_ERROR &&
      !this.isLoading &&
      !this.tableData.length
    );
  }
  onRetry() {
    this.getList(null, false);
    this.$emit("retry");
  }

  disconnectObservers() {
    return new Promise((resolve) => {
      if (this.topRankObserver) this.topRankObserver.disconnect();
      if (this.currentRankObserver) this.currentRankObserver.disconnect();
      if (this.bottomObserver) this.bottomObserver.disconnect();
      if (this.nearestTopRankObserver) this.nearestTopRankObserver.disconnect();
      resolve(true);
    });
  }
}

export default PosLeaderboardTable;
